import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useApi } from '../../lib/api';

export default function PartnerTier() {
  const api = useApi();
  const { partner } = useOutletContext();
  const [tiers, setTiers] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    api.partnerListTiers()
      .then((r) => alive && setTiers(r.items || []))
      .catch((err) => alive && setError(err.response?.data?.error || err.message));
    return () => { alive = false; };
  }, [api]);

  if (error) return <div className="admin-banner error" style={{ margin: '20px 36px' }}>{error}</div>;
  if (!partner) return <p className="muted" style={{ margin: '20px 36px' }}>Loading…</p>;

  const currentIdx = tiers.findIndex((t) => t.id === partner.tier_id);
  const nextTier = currentIdx >= 0 ? tiers[currentIdx + 1] : null;
  // Tier targets count PAYING clients (active clients with at least one payment).
  const payingClients = partner?.paying_client_count || 0;
  const eligibleTier = partner?.eligible_tier || null;

  const targetNumber = nextTier?.target_clients ?? null;
  const firstTarget = tiers.find((t) => t.target_clients) || null;
  const progressPct = targetNumber ? Math.min(100, Math.round((payingClients / targetNumber) * 100)) : null;
  const targetReached = !!eligibleTier || (targetNumber != null && payingClients >= targetNumber);

  return (
    <>
      <header className="bar">
        <div>
          <h1>My Tier</h1>
          <p>Your commission rate and how to reach the next one.</p>
        </div>
      </header>

      <section className="view">
        {!partner.tier_id ? (
          <div className="admin-banner warn">
            <strong>Tier: Not Assigned</strong> — platform admin has not assigned you a commission tier yet. You will not earn commission until one is assigned.
            {eligibleTier ? (
              <div style={{ marginTop: 6 }}>
                You qualify for <strong>{eligibleTier.name}</strong> ({payingClients} paying client{payingClients === 1 ? '' : 's'}) — waiting for admin approval.
              </div>
            ) : firstTarget ? (
              <div style={{ marginTop: 6 }}>
                {payingClients} of {firstTarget.target_clients} paying clients toward {firstTarget.name}.
              </div>
            ) : null}
          </div>
        ) : (
          <>
          {partner.tier_is_active === 0 && (
            <div className="admin-banner warn" style={{ marginBottom: 16 }}>
              Your tier <strong>{partner.tier_name}</strong> has been deactivated, so new client payments don't earn commission right now. The platform admin will move you to an active tier.
            </div>
          )}
            <div className="panel ladder">
              <h3 style={{ fontSize: 16, fontWeight: 600 }}>Your commission ladder</h3>
              <p style={{ fontSize: 13.5, color: 'var(--text-2)', marginTop: 4 }}>
                Reach a tier's target of paying clients and the platform admin reviews your upgrade. Commission you already earned under an earlier tier never changes.
              </p>

              <div className="ladder-track">
                {tiers.map((t, i) => (
                  <div key={t.id} className={`rung${t.id === partner.tier_id ? ' now' : i < currentIdx ? ' done' : ''}`}>
                    {t.id === partner.tier_id && <span className="badge-now">You are here</span>}
                    <h4>{t.name}</h4>
                    <span className="pct num">{t.commission_percentage}%</span>
                    {(() => {
                      const target = t.target_clients
                        ? `Reach ${t.target_clients} paying client${t.target_clients === 1 ? '' : 's'}`
                        : (t.target_description || 'Assigned by admin');
                      return <small title={target}>{target}</small>;
                    })()}
                  </div>
                ))}
              </div>

              {nextTier && (
                <div className="progress">
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <b style={{ fontSize: 14.5, fontWeight: 600 }}>
                      {targetReached
                        ? `${(eligibleTier || nextTier).name} target reached (${payingClients} paying client${payingClients === 1 ? '' : 's'})`
                        : targetNumber
                          ? `${payingClients} of ${targetNumber} paying clients toward your ${nextTier.name} target`
                          : `Working toward ${nextTier.name}`}
                    </b>
                    {targetNumber && !targetReached && (
                      <span className="muted" style={{ marginLeft: 'auto', fontSize: 13 }}>
                        {Math.max(0, targetNumber - payingClients)} to go
                      </span>
                    )}
                  </div>
                  {progressPct != null && <div className="pbar"><i style={{ width: `${progressPct}%` }} /></div>}
                  <p style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
                    {targetReached
                      ? `Waiting for admin approval to move you to ${(eligibleTier || nextTier).name}.`
                      : targetNumber
                        ? 'A paying client is an active client with at least one completed payment.'
                        : 'This tier is assigned manually by the platform admin.'}
                  </p>
                </div>
              )}
            </div>

            <div className="note">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9"/><path d="M12 16v-5M12 8h.01"/></svg>
              <span>
                Your rate applies at the moment each client payment is recorded.
                {partner.tier_expires_at && ` If your tier changes on ${new Date(partner.tier_expires_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}, payments before that date still pay ${partner.tier_commission_percentage}%.`}
              </span>
            </div>
          </>
        )}
      </section>
    </>
  );
}
